import { useState } from 'react'
import csrfHeaders from 'src/utils/csrfHeaders'
import extractUtmParams from 'src/utils/extractUtmParams'

export const genericError = i18n => ({ base: [i18n.t('errors.donation_error')] })

const draftProps = ({
  i18n, order, donation, references, principalOnly, plugins, canadianReceipt, giftAid,
  partner, partnerItem, projectId, widget, session,
  tribute, donationTags, embeddedOn, orderProps, employerMatching,
}) => {
  // principalOnly = the donation carries no extras. Off-platform
  // settlement (crypto / stock / DAF when they land on drafts) supports
  // neither tipping nor fee-covering, so callers just declare intent.
  // amount_entered is nulled too — for these flows, the donor's typed
  // fiat is aspirational (crypto: rates fluctuate between entry and
  // send, donor may also choose to send a different amount; stock/DAF:
  // the executed amount comes from the third-party processor, not the
  // donor's input). Better to leave the column NULL than to record a
  // donor-typed number that may not match what actually arrived.
  const tip = principalOnly ? 0 : donation.tip
  const tipPresent = !!Number(tip)
  const coverCosts = !principalOnly && !tipPresent && donation.state.coverCosts
  const amount = coverCosts ? donation.total : donation.state.amount
  const amountEntered = principalOnly ? null : donation.state.amount

  return {
    id:             widget.token,
    template:       widget.template,
    partner_key:    partner.key,
    external_id:    partnerItem.external_id,
    ein:            partnerItem.beneficiary.ein,
    enable_session: !session.collect_email,
    donation_tags:  donationTags,
    embedded_on:    embeddedOn,
    // donations#create resolves the locale from this param and nothing
    // else — `orders` has no locale column and the POST carries no other
    // signal — so it has to keep being sent. Same as the Stripe intents
    // POST in useStripeServices.
    locale:         i18n.locale,
    ...references,
    order: {
      ...extractUtmParams(embeddedOn),
      ...tribute.draftOrderProps(),
      ...orderProps,
      user_first_name:    order.firstName,
      user_last_name:     order.lastName,
      user_phone_number:  order.phone,
      address:            order.address,
      email:              order.email,
      partner_id:         partner.id,
      external_id:        partnerItem.external_id,
      project_id:         projectId,
      shares:             donation.shares,
      currency:           donation.currency.id,
      donation_amount:    amount,
      tip_amount:         tip,
      cover_fees:         coverCosts,
      amount_entered:     amountEntered,
      canadian_receipt:   canadianReceipt?.value,
      gift_aid:           giftAid?.value,
      ...employerMatching?.orderProps?.(),
      metadata:           order.metadata,
      plugin_data:        plugins.dataParam,
      // TODO: remove after upgrading plugins
      udf_label:          donation.state.udf_label,
      udf_value:          donation.state.udf_value,
    }
  }
}

const useRecord = ({
  i18n, order, widget, partner, pledge_id, pdt, session, donationTags, embeddedOn,
  partnerItem, projectId, donation, plugins, tribute, canadianReceipt, giftAid,
  donationsPath, employerMatching,
}) => {
  const [ processing, setProcessing ] = useState(false)
  const [ references, setReferences ] = useState({ pledge_id, pdt })

  const saveDraft = async ({ principalOnly, order: orderProps, contact } = {}) => {
    const params = draftProps({
      i18n, order: { ...order, ...donation.state, ...contact }, references, principalOnly,
      donation, partner, partnerItem, projectId, widget,
      session, tribute, donationTags, embeddedOn, plugins,
      orderProps, canadianReceipt, giftAid, employerMatching,
    })

    try {
      const response = await fetch(donationsPath, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify(params)
      })
      const {
        token,
        order: serializedDonation,
        ...errors
      } = await response.json()

      if (response.ok) {
        setReferences({ order_id: token })
        return { token, serializedDonation }
      } else {
        return { errors }
      }
    } catch (error) {
      console.error('Error saving draft:', error)
      return { errors: genericError(i18n) }
    }
  }

  return { processing, setProcessing, saveDraft }
}

export default useRecord
